/**
 * Keystore —— 修复原型缺陷 **B2**（libp2p 私钥明文 base64 存 localStorage）与 **B5**（无口令保护）。
 *
 * 口径（ARCH §8.3，全站唯一）：
 *  - 口令 → 密钥：`PBKDF2-HMAC-SHA256`，**310000 次**，salt **16B** 随机，输出 256bit
 *  - 加密：`AES-256-GCM`，iv **12B** 随机，密文与 tag 一起存进 `payloadB64`
 *  - 私钥 / 助记词**永不以明文落盘**；seal 后即为密文入 IndexedDB
 */

import { randomBytes, zeroBytes } from './noble';
import { bytesToBase64, base64ToBytes } from '@/lib/format';
import { MeshLinkError } from '@/lib/result';

export const KDF_ITERATIONS = 310_000;
export const SALT_BYTES = 16;
export const IV_BYTES = 12;
export const KEY_BITS = 256;

export const KEYSTORE_VERSION = 1;

/** seal 进 keystore 的明文载荷（内存中存在，落盘即密文）。 */
export interface KeyBundle {
  /** base64，32B */
  signSeed: string;
  encSeed: string;
  peerSeed: string;
  /** 助记词（加密存储，供「查看助记词」二次口令校验后展示） */
  mnemonic: string[];
}

/** 落盘的密文 keystore 结构（IndexedDB `keystore` store）。 */
export interface EncryptedKeystore {
  id: 'default';
  version: number;
  did: string;
  address: string;
  peerId: string;
  kdf: { name: 'PBKDF2-HMAC-SHA256'; iterations: number; saltB64: string };
  cipher: { name: 'AES-256-GCM'; ivB64: string };
  /** base64( ciphertext ‖ tag ) */
  payloadB64: string;
  createdAt: number;
  updatedAt: number;
}

/** 导出的加密身份包（换设备恢复用，仍需原口令或新口令解开）。 */
export interface EncryptedIdentityBackup {
  format: 'meshlink-identity/1';
  keystore: EncryptedKeystore;
  meta: { did: string; address: string; peerId: string; exportedAt: number };
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function subtle(): SubtleCrypto {
  const ctx = globalThis.crypto;
  if (!ctx?.subtle) throw new MeshLinkError('E_STORAGE_UNAVAILABLE', '当前环境不支持 WebCrypto');
  return ctx.subtle;
}

/** 口令 → AES-256-GCM 主密钥。 */
async function deriveKey(password: string, salt: Uint8Array, iterations = KDF_ITERATIONS): Promise<CryptoKey> {
  const baseKey = await subtle().importKey('raw', encoder.encode(password), 'PBKDF2', false, ['deriveKey']);
  return subtle().deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: KEY_BITS },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** 加密 keystore。 */
export async function sealKeystore(
  bundle: KeyBundle,
  password: string,
  identity: { did: string; address: string; peerId: string },
): Promise<EncryptedKeystore> {
  if (!password) throw new MeshLinkError('E_INVALID_INPUT', '口令不能为空');
  const salt = randomBytes(SALT_BYTES);
  const iv = randomBytes(IV_BYTES);
  const key = await deriveKey(password, salt);
  const plaintext = encoder.encode(JSON.stringify(bundle));
  const sealed = new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, plaintext as BufferSource));
  const now = Date.now();
  return {
    id: 'default',
    version: KEYSTORE_VERSION,
    did: identity.did,
    address: identity.address,
    peerId: identity.peerId,
    kdf: { name: 'PBKDF2-HMAC-SHA256', iterations: KDF_ITERATIONS, saltB64: bytesToBase64(salt) },
    cipher: { name: 'AES-256-GCM', ivB64: bytesToBase64(iv) },
    payloadB64: bytesToBase64(sealed),
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * 解密 keystore。口令错误 / 数据被篡改 → **必然**抛 `E_PASSWORD_INVALID`
 * （AES-GCM 的 tag 校验同时覆盖这两类情况）。
 */
export async function openKeystore(keystore: EncryptedKeystore, password: string): Promise<KeyBundle> {
  if (!password) throw new MeshLinkError('E_PASSWORD_INVALID');
  try {
    const salt = base64ToBytes(keystore.kdf.saltB64);
    const iv = base64ToBytes(keystore.cipher.ivB64);
    const sealed = base64ToBytes(keystore.payloadB64);
    const key = await deriveKey(password, salt, keystore.kdf.iterations || KDF_ITERATIONS);
    const plaintext = new Uint8Array(
      await subtle().decrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, sealed as BufferSource),
    );
    const bundle = JSON.parse(decoder.decode(plaintext)) as KeyBundle;
    if (!bundle?.signSeed || !bundle?.encSeed || !bundle?.peerSeed) {
      throw new MeshLinkError('E_PASSWORD_INVALID', 'keystore 内容不完整');
    }
    return bundle;
  } catch (err) {
    if (err instanceof MeshLinkError) throw err;
    throw new MeshLinkError('E_PASSWORD_INVALID', undefined, err);
  }
}

/** 改口令：先解开再重新 seal（不需要重新派生密钥）。 */
export async function reencryptKeystore(
  keystore: EncryptedKeystore,
  oldPassword: string,
  newPassword: string,
): Promise<EncryptedKeystore> {
  const bundle = await openKeystore(keystore, oldPassword);
  const next = await sealKeystore(
    bundle,
    newPassword,
    { did: keystore.did, address: keystore.address, peerId: keystore.peerId },
  );
  next.createdAt = keystore.createdAt;
  return next;
}

/** 导出加密身份包（JSON 字符串）。 */
export function exportIdentityBackup(keystore: EncryptedKeystore): string {
  const backup: EncryptedIdentityBackup = {
    format: 'meshlink-identity/1',
    keystore,
    meta: { did: keystore.did, address: keystore.address, peerId: keystore.peerId, exportedAt: Date.now() },
  };
  return JSON.stringify(backup, null, 2);
}

/** 解析加密身份包。 */
export function parseIdentityBackup(raw: string): EncryptedKeystore {
  const parsed = JSON.parse(raw) as EncryptedIdentityBackup;
  if (parsed?.format !== 'meshlink-identity/1' || !parsed.keystore?.payloadB64) {
    throw new MeshLinkError('E_INVALID_INPUT', '身份包格式不正确');
  }
  return parsed.keystore;
}

/** 尽最大努力清零内存中的密钥材料。 */
export function wipeBundle(bundle: KeyBundle): void {
  try {
    zeroBytes(base64ToBytes(bundle.signSeed));
    zeroBytes(base64ToBytes(bundle.encSeed));
    zeroBytes(base64ToBytes(bundle.peerSeed));
  } catch {
    // base64 非法时忽略（本函数只做尽最大努力清零）
  }
}
